import { useRef } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay/legacy.js';
import { getConsentedBuyerId, getGuestId } from 'dibs-cookie-jar';
import { hasPersonalizedFilterQueryParam } from '../../shared/helpers/personalizedFiltersHelpers.js';
import { handleSbUserCountryCode } from '../helpers/handleSbUserCountryCode.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';

/* Components */
import { handlePageRefetchVariableOnUserChange } from '../../shared/handlePageRefetchVariableOnUserChange.ts';
import { SbSharedRefetchContainer } from '../SbSharedRefetchContainer/SbSharedRefetchContainer.jsx';
import { SbRespComponents } from '../SbRespComponents/SbRespComponents.jsx';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead.jsx';
import {
    RecentlyViewedItemsProvider,
    TABLET,
    DESKTOP,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider.ts';
import { SbSharedFavoritesNudgeToast } from '../../shared/SharedFavoritesNudge/SbSharedFavoritesNudgeToast/SbSharedFavoritesNudgeToast.tsx';

/* Hooks */
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSharedUserAndRelayVarsLoader } from '../../shared/SharedUserAndRelayVarLoader.tsx';
import { useSearchCorrections } from '../useSearchCorrections.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { useSbSharedPageTracking } from '../hooks/useSbSharedPageTracking.ts';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap.ts';

const SbRespLayoutComponent = ({
    viewer,
    reinitTracking,
    relay,
    runNotificationQueue = () => {},
}) => {
    const { itemSearch, regionalInfo = null, shippingFilters = null, user = null } = viewer;
    const userId = user?.serviceId;
    const relayVars = useRef({});
    const {
        isTablet,
        isItemFavoritingVariant,
        isItemFavoritingFeb2026Variant,
        isItemFavoritingFeb2026Control,
    } = useServerVarsContext();
    const relayVariables = useSbSelector(store => store.relayVariables.variables);
    const [searchCorrectionTerm] = useSearchCorrections({ itemSearch });

    useDwelledOnPage(PAGE_TYPE.SEARCH);

    function doRefetch(variables, renderVariables, callback) {
        relayVars.current = {
            ...relayVars.current,
            ...variables,
            userCountryCode: handleSbUserCountryCode({
                userCountryCode: variables?.userCountryCode,
                fetchSb: variables?.fetchSb,
            }),
        };
        relay.refetch(
            relayVars.current,
            { ...relayVars.current, ...renderVariables, fetchSb: true },
            callback
        );
    }

    function onUserRefetch(variables, ...args) {
        const { isClient, fetchSbOnUserChange } = variables || {};
        const hasPersonalizedFilter = isClient && hasPersonalizedFilterQueryParam();

        //Personal-rerank needs to refetch s&b after login
        const fetchSb = handlePageRefetchVariableOnUserChange(variables) || fetchSbOnUserChange;

        const { displayUriRef } = itemSearch;
        const { followSearchPages } = relayVariables || {};

        doRefetch(
            {
                ...variables,
                fetchSb,
                skipUser: false,
                hasPersonalizedFilter,
                followSearchPages: displayUriRef ? [displayUriRef] : followSearchPages,
            },
            ...args
        );
    }

    function handleSbRefetch(variables, renderVariables, callback) {
        /*
         * UserData is required for sponsored listings, add it to relay vars for refetch,
         * cause initial page could be not eligible for sponsored items, but subsequent page could (eg. by removing filters).
         * graphql will decide if sponsored items should be added in the end.
         */
        const userData = {
            personalizationId: getConsentedBuyerId(document.cookie) || '',
            guestId: getGuestId(document.cookie) || '',
            includeSponsoredItems: true,
        };

        doRefetch({ ...variables, ...userData, fetchSb: true }, renderVariables, () => {
            callback();

            runNotificationQueue();
        });
    }

    useSharedUserAndRelayVarsLoader({ onUserRefetch });

    useSbSharedPageTracking({
        viewer,
        itemSearch,
        user,
        reinitTracking,
        refineMenuFiltersMap: sbRespRefineMenuFiltersMap,
        searchCorrectionTerm,
    });

    return (
        <RecentlyViewedItemsProvider deviceType={isTablet ? TABLET : DESKTOP}>
            <SbSharedHead itemSearch={itemSearch} />
            <SbSharedRefetchContainer
                viewer={viewer}
                itemSearch={itemSearch}
                regionalInfo={regionalInfo}
                shippingFilters={shippingFilters}
                onSbRefetch={handleSbRefetch}
                shouldShowSpinner={false}
            />
            <SbRespComponents
                viewer={viewer}
                itemSearch={itemSearch}
                user={user}
                isClient={!!relayVars.current.isClient}
                shippingFilters={shippingFilters}
            />
            <SbSharedFavoritesNudgeToast
                userId={userId}
                isItemFavoritingVariant={isItemFavoritingVariant}
                isItemFavoritingFeb2026Variant={isItemFavoritingFeb2026Variant}
                isItemFavoritingFeb2026Control={isItemFavoritingFeb2026Control}
            />
        </RecentlyViewedItemsProvider>
    );
};

SbRespLayoutComponent.propTypes = {
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    runNotificationQueue: PropTypes.func.isRequired,
    reinitTracking: PropTypes.func,
};

export const SbRespLayout = createRefetchContainer(
    SbRespLayoutComponent,
    {
        viewer: graphql`
            fragment SbRespLayout_viewer on Viewer
            @argumentDefinitions(
                hasPersonalizedFilter: { type: "Boolean", defaultValue: false }
                userZipCode: { type: "String", defaultValue: "" }
                fetchRegionalInfo: { type: "Boolean", defaultValue: false }
            ) {
                ...SbRespComponents_viewer
                ...useSbSharedPageTracking_viewer
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    regionsList: $regionsList
                    includeSponsoredItems: $includeSponsoredItems
                    priceBookName: $priceBookName
                    numOfItemsToInflate: $numOfItemsToInflate
                ) {
                    displayUriRef
                    ...useSearchCorrections_itemSearch
                    ...SbSharedRefetchContainer_itemSearch
                    ...useSbSharedPageTracking_itemSearch
                    ...SbSharedHead_itemSearch
                    ...SbRespComponents_itemSearch @include(if: $fetchSb)
                    ...SbRespFollowSearchBanner_itemSearch
                }
                user(userId: $userId) @include(if: $fetchUser) {
                    serviceId
                    ...useSbSharedPageTracking_user
                    ...SbRespComponents_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespComponents_shippingFilters
                }
            }
        `,
    },
    graphql`
        query SbRespLayoutRefetchQuery(
            $userId: String = ""
            $personalizationId: String = ""
            $guestId: String = ""
            $rerankUserId: String = ""
            $rerankGuestId: String = ""
            $fetchUser: Boolean!
            $fetchSb: Boolean!
            $skipUser: Boolean = true
            $isTrade: Boolean!
            $includeSponsoredItems: Boolean = false
            $uriRef: String
            $originalUrl: String
            $first: Int!
            $page: Int!
            $localeOrigin: String = ""
            $showSeller: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $contentModuleId: String = ""
            $previewId: String = ""
            $regions: String = ""
            $hasPersonalizedFilter: Boolean!
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $disableForceFacet: Boolean = true
            $isClient: Boolean!
            $disableNonParameterizedUrlRedirects: Boolean = false # Only used for static SB
            $fetchTileVideo: Boolean = false
            # AUCTIONS-1264 - Changing this argument might break carousel UI, make sure the requirements in the ticket description are met.
            $interiorPhotosCount: Int = 10
            $marketTrendsUrl: String = ""
            $priceBookName: String
            $regionsList: [String]
            $pageDisplayEnum: PageDisplayEnum = searchAndBrowse
            $numOfItemsToInflate: Int
        ) {
            viewer {
                ...SbRespComponents_viewer
                ...useSbSharedPageTracking_viewer
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    regionsList: $regionsList
                    priceBookName: $priceBookName
                    includeSponsoredItems: $includeSponsoredItems
                    numOfItemsToInflate: $numOfItemsToInflate
                ) @include(if: $fetchSb) {
                    displayUriRef
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbRespComponents_itemSearch
                    ...SbSharedHead_itemSearch
                    ...useSbSharedPageTracking_itemSearch
                }
                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...useSbSharedPageTracking_user
                    ...SbRespComponents_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespComponents_shippingFilters
                }
            }
        }
    `
);
